"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.initRealtimeSync = void 0;
const RealtimeService_1 = require("./RealtimeService");
const useStore_1 = require("../store/useStore");
const AudioService_1 = require("./AudioService");
const MusicFeatureService_1 = require("./MusicFeatureService");
const SoundCloudService_1 = require("./SoundCloudService");
const LastFmService_1 = require("./LastFmService");
let initialized = false;
const initRealtimeSync = () => {
    if (initialized)
        return;
    initialized = true;
    console.log('[RealtimeSync] Initializing live socket listeners...');
    const socket = RealtimeService_1.realtimeService.init();
    // 1. Live Chat Messages
    socket.on('chat:message', (rawMsg) => {
        if (!rawMsg)
            return;
        const store = useStore_1.useStore.getState();
        // Channel Message
        if (rawMsg.channelId) {
            const channelId = String(rawMsg.channelId);
            if (store.selectedChannelId === channelId) {
                const currentUserId = String(store.currentUser?.id || '');
                const authorId = String(rawMsg.authorId || rawMsg.userId || '');
                // If sent by ourselves, avoid duplicate/echo since it is already rendered optimistically
                if (authorId === currentUserId) {
                    const alreadyExists = store.messages.some((m) => String(m.id) === String(rawMsg.id) || (m.clientKey && m.clientKey === rawMsg.clientKey));
                    if (alreadyExists)
                        return;
                }
                const msgId = String(rawMsg.id || Date.now());
                const exists = store.messages.some((m) => String(m.id) === msgId || (m.clientKey && m.clientKey === rawMsg.clientKey));
                if (!exists) {
                    const newMsg = {
                        id: msgId,
                        clientKey: rawMsg.clientKey || msgId,
                        content: rawMsg.content,
                        authorId: authorId,
                        authorName: rawMsg.authorName || 'Kullanıcı',
                        authorAvatar: rawMsg.authorAvatar,
                        channelId: channelId,
                        timestamp: new Date(rawMsg.timestamp || Date.now()),
                        parentId: rawMsg.parentId,
                        attachments: rawMsg.attachments || [],
                    };
                    useStore_1.useStore.setState((s) => ({
                        messages: [...s.messages, newMsg],
                    }));
                    if (authorId !== currentUserId) {
                        AudioService_1.audioService.playMessageReceived();
                    }
                }
            }
        }
        // Direct Message (DM)
        if (rawMsg.sender_id && rawMsg.receiver_id) {
            const currentUserId = String(store.currentUser?.id || '');
            const selectedDMUserId = String(store.selectedDMUserId || '');
            const isCurrentDM = (String(rawMsg.sender_id) === selectedDMUserId && String(rawMsg.receiver_id) === currentUserId) ||
                (String(rawMsg.sender_id) === currentUserId && String(rawMsg.receiver_id) === selectedDMUserId);
            if (isCurrentDM) {
                if (String(rawMsg.sender_id) === currentUserId) {
                    const alreadyExists = store.directMessages.some((m) => String(m.id) === String(rawMsg.id) || (m.clientKey && m.clientKey === rawMsg.clientKey));
                    if (alreadyExists)
                        return;
                }
                const dmId = String(rawMsg.id || Date.now());
                const exists = store.directMessages.some((m) => String(m.id) === dmId || (m.clientKey && m.clientKey === rawMsg.clientKey));
                if (!exists) {
                    useStore_1.useStore.setState((s) => ({
                        directMessages: [...s.directMessages, { ...rawMsg, clientKey: rawMsg.clientKey || dmId }],
                    }));
                    if (String(rawMsg.sender_id) !== currentUserId) {
                        AudioService_1.audioService.playMessageReceived();
                    }
                }
            }
        }
    });
    // 2. Typing Indicators
    socket.on('chat:typing', (payload) => {
        if (!payload)
            return;
        const store = useStore_1.useStore.getState();
        const currentUserId = String(store.currentUser?.id || '');
        const senderId = String(payload.currentUserId || payload.userId || '');
        if (!senderId || senderId === currentUserId)
            return;
        const username = payload.username || 'Kullanıcı';
        const isTyping = !!payload.isTyping;
        if (payload.channelId) {
            const channelId = String(payload.channelId);
            if (isTyping) {
                store.addTypingUser(channelId, senderId, username);
            }
            else {
                store.removeTypingUser(channelId, senderId);
            }
        }
        else if (payload.dmUserId || payload.currentUserId) {
            const dmKey = `dm_${senderId}`;
            if (isTyping) {
                store.addTypingUser(dmKey, senderId, username);
            }
            else {
                store.removeTypingUser(dmKey, senderId);
            }
        }
    });
    // 3. Live Delete
    socket.on('chat:delete', ({ messageId }) => {
        if (!messageId)
            return;
        useStore_1.useStore.setState((state) => ({
            messages: state.messages.filter((m) => String(m.id) !== String(messageId)),
            directMessages: state.directMessages.filter((m) => String(m.id) !== String(messageId)),
        }));
    });
    // 4. Live Edit
    socket.on('chat:edit', ({ messageId, content }) => {
        if (!messageId)
            return;
        useStore_1.useStore.setState((state) => ({
            messages: state.messages.map((m) => String(m.id) === String(messageId) ? { ...m, content, edited: true } : m),
            directMessages: state.directMessages.map((m) => String(m.id) === String(messageId) ? { ...m, content, edited: true } : m),
        }));
    });
    // 5. Live Reactions
    socket.on('chat:reaction', ({ messageId, emoji, userId }) => {
        if (!messageId || !emoji || !userId)
            return;
        const store = useStore_1.useStore.getState();
        store.addReaction(messageId, emoji, userId);
    });
    // 6. User Registration on connect / reconnect
    const registerCurrentUser = () => {
        const store = useStore_1.useStore.getState();
        if (store.currentUser?.id) {
            socket.emit('user:register', { userId: String(store.currentUser.id) });
        }
    };
    registerCurrentUser();
    socket.on('connect', registerCurrentUser);
    // 7. Live 1-on-1 Voice & Video Call Signaling
    socket.on('call:incoming', (payload) => {
        console.log('[RealtimeSync] Incoming call received:', payload);
        const store = useStore_1.useStore.getState();
        // Do not show incoming call if we are already in another voice session
        if (store.voiceConnection.status === 'connected')
            return;
        AudioService_1.audioService.playCallRingtone();
        store.setIncomingCall({
            callerId: String(payload.callerId),
            callerName: payload.callerName || 'Kullanıcı',
            callerAvatar: payload.callerAvatar,
            channelId: payload.channelId,
            isVideo: !!payload.isVideo,
        });
    });
    socket.on('call:accepted', (payload) => {
        console.log('[RealtimeSync] Call accepted:', payload);
        AudioService_1.audioService.stopCallRingtone();
    });
    socket.on('call:rejected', (payload) => {
        console.log('[RealtimeSync] Call rejected:', payload);
        AudioService_1.audioService.stopCallRingtone();
        const store = useStore_1.useStore.getState();
        store.setIncomingCall(null);
    });
    socket.on('call:ended', (payload) => {
        console.log('[RealtimeSync] Call ended:', payload);
        AudioService_1.audioService.stopCallRingtone();
        const store = useStore_1.useStore.getState();
        store.setIncomingCall(null);
    });
    // 8. Live Friend Requests & Sync
    socket.on('friend:request', (payload) => {
        console.log('[RealtimeSync] Friend request received:', payload);
        const store = useStore_1.useStore.getState();
        store.fetchFriends();
        AudioService_1.audioService.playMessageReceived();
    });
    socket.on('friend:accepted', (payload) => {
        console.log('[RealtimeSync] Friend request accepted:', payload);
        const store = useStore_1.useStore.getState();
        store.fetchFriends();
    });
    socket.on('friend:sync', (payload) => {
        console.log('[RealtimeSync] Friend sync received:', payload);
        const store = useStore_1.useStore.getState();
        store.fetchFriends();
    });
    // 9. Real-time Profile Anthem / Song Sync
    socket.on('profile_song:changed', (payload) => {
        if (payload && payload.userId) {
            MusicFeatureService_1.musicFeatureService.syncFromRemote(payload.userId, payload.song || null);
        }
    });
    // 10. Real-time Music Playing Broadcast (Last.fm / SoundCloud)
    socket.on('user:music_update', (payload) => {
        if (payload && payload.userId) {
            SoundCloudService_1.soundCloudService.setUserTrack(payload.userId, payload.track || null);
        }
    });
    // 11. Real-time Bot Voice & Soundboard & Music Playback
    // ─── IMPORTANT ARCHITECTURE ───
    // Music events (music:play, music:stop, music:pause, music:resume) are scoped
    // to voice channel rooms on the server side. Only users IN the voice channel
    // receive these events, making audio work like a real Discord bot.
    // Soundboard (voice:soundboard) is for short SFX only, not music.
    let activeAudio = null;
    const setBotSpeaking = (botUid, speaking) => {
        const targetUid = String(botUid || '370846327506079745');
        useStore_1.useStore.setState((prev) => {
            const isBotMatch = (p) => String(p.userId) === targetUid ||
                String(p.id) === targetUid ||
                String(p.id) === `voice-bot-${targetUid}` ||
                String(p.id) === `voice-${targetUid}` ||
                (Boolean(p.isBot) && !p.isLocal) ||
                (typeof p.displayName === 'string' && p.displayName.toLowerCase().includes('bot') && !p.isLocal);
            const existsInVoice = prev.voiceParticipants.some(isBotMatch);
            const activeChId = prev.voiceConnection?.channelId || '6';
            const botParticipant = {
                id: `voice-bot-${targetUid}`,
                userId: targetUid,
                displayName: 'Meci Test Bot',
                avatar: 'https://api.dicebear.com/7.x/bottts/svg?seed=mecitestbot',
                accentColor: '#5865f2',
                muted: false,
                deafened: false,
                cameraEnabled: false,
                screenSharing: false,
                speaking,
                isLocal: false,
                isBot: true,
            };
            // 1. Update voiceParticipants
            let nextVoiceParticipants = existsInVoice
                ? prev.voiceParticipants.map((p) => isBotMatch(p) ? { ...p, speaking, isBot: true } : p)
                : speaking
                    ? [...prev.voiceParticipants, botParticipant]
                    : prev.voiceParticipants;
            // 2. Update serverVoiceStates for sidebar channel list
            const prevChUsers = (prev.serverVoiceStates && prev.serverVoiceStates[activeChId]) || [];
            const existsInServerState = prevChUsers.some(isBotMatch);
            const nextChUsers = existsInServerState
                ? prevChUsers.map((u) => (isBotMatch(u) ? { ...u, speaking, isBot: true } : u))
                : speaking
                    ? [...prevChUsers, botParticipant]
                    : prevChUsers;
            const nextServerVoiceStates = {
                ...(prev.serverVoiceStates || {}),
                [activeChId]: nextChUsers,
            };
            return {
                voiceParticipants: nextVoiceParticipants,
                serverVoiceStates: nextServerVoiceStates,
            };
        });
    };
    /** Stop any currently playing bot music audio */
    const stopBotMusic = () => {
        if (activeAudio) {
            activeAudio.pause();
            activeAudio.currentTime = 0;
            activeAudio = null;
            setBotSpeaking('370846327506079745', false);
        }
    };
    /** Play a music stream URL from the bot, with optional seek position */
    const playBotMusic = (streamUrl, payload, seekSec) => {
        if (!streamUrl)
            return;
        // Strict voice channel scoping: only play if user is actively in the target voice channel
        const activeVoice = useStore_1.useStore.getState().voiceConnection;
        const targetChannel = String(payload.channelId || '6');
        if (!activeVoice || String(activeVoice.channelId) !== targetChannel) {
            console.log(`[BotMusic] User is not in voice channel ${targetChannel}, skipping audio playback`);
            return;
        }
        try {
            if (activeAudio) {
                activeAudio.pause();
                activeAudio.currentTime = 0;
            }
            const botUid = String(payload.userId || '370846327506079745');
            const audio = new Audio(streamUrl);
            audio.crossOrigin = 'anonymous';
            audio.volume = payload.volume !== undefined ? Math.max(0, Math.min(1, payload.volume / 100)) : 0.8;
            activeAudio = audio;
            audio.onplay = () => setBotSpeaking(botUid, true);
            audio.onpause = () => setBotSpeaking(botUid, false);
            audio.onended = () => {
                setBotSpeaking(botUid, false);
                activeAudio = null;
            };
            // If we need to seek to a position (newcomer sync)
            if (seekSec && seekSec > 0) {
                audio.addEventListener('loadedmetadata', () => {
                    if (audio.duration && seekSec < audio.duration) {
                        audio.currentTime = seekSec;
                    }
                }, { once: true });
            }
            setBotSpeaking(botUid, true);
            audio.play().catch((err) => {
                console.warn('[BotMusic] Autoplay blocked, waiting for user interaction:', err);
                const unlock = () => {
                    audio.play().catch(() => { });
                    setBotSpeaking(botUid, true);
                    window.removeEventListener('click', unlock);
                    window.removeEventListener('keydown', unlock);
                };
                window.addEventListener('click', unlock, { once: true });
                window.addEventListener('keydown', unlock, { once: true });
            });
        }
        catch (err) {
            console.error('[BotMusic] Playback failed:', err);
        }
    };
    // ─── Music Events (scoped to voice channel by server) ───
    socket.on('music:play', (payload) => {
        if (!payload)
            return;
        const streamUrl = payload.audioUrl || payload.url;
        console.log('[BotMusic] music:play received:', payload.soundName || streamUrl);
        playBotMusic(streamUrl, payload);
    });
    socket.on('music:stop', () => {
        console.log('[BotMusic] music:stop received');
        stopBotMusic();
    });
    socket.on('music:pause', () => {
        console.log('[BotMusic] music:pause received');
        if (activeAudio) {
            activeAudio.pause();
            setBotSpeaking('370846327506079745', false);
        }
    });
    socket.on('music:resume', () => {
        console.log('[BotMusic] music:resume received');
        if (activeAudio) {
            setBotSpeaking('370846327506079745', true);
            activeAudio.play().catch((e) => console.warn('[BotMusic] Resume error:', e));
        }
    });
    // ─── Newcomer Music Sync: play from the right position ───
    socket.on('music:current-state', (payload) => {
        if (!payload || !payload.track)
            return;
        const activeVoice = useStore_1.useStore.getState().voiceConnection;
        const targetChannel = String(payload.channelId || '6');
        if (!activeVoice || String(activeVoice.channelId) !== targetChannel) {
            return;
        }
        const streamUrl = payload.track.url;
        const elapsedSec = payload.elapsedSec || 0;
        console.log(`[BotMusic] Syncing to current music: ${payload.track.title} (elapsed ${elapsedSec}s)`);
        playBotMusic(streamUrl, { userId: payload.userId, channelId: targetChannel, volume: 80 }, elapsedSec);
    });
    // Synchronize music when user joins or leaves a voice channel
    let lastChannelId = null;
    useStore_1.useStore.subscribe((state) => {
        const currentChannelId = state.voiceConnection?.channelId ? String(state.voiceConnection.channelId) : null;
        if (currentChannelId === lastChannelId)
            return;
        const prevChannelId = lastChannelId;
        lastChannelId = currentChannelId;
        if (prevChannelId && !currentChannelId) {
            // User left voice channel -> stop local audio playback
            console.log('[BotMusic] User disconnected from voice channel, stopping audio');
            if (activeAudio) {
                activeAudio.pause();
                activeAudio.currentTime = 0;
                activeAudio = null;
            }
        }
        else if (currentChannelId && currentChannelId !== prevChannelId) {
            // User entered or switched voice channel
            if (activeAudio) {
                activeAudio.pause();
                activeAudio.currentTime = 0;
                activeAudio = null;
            }
            console.log(`[BotMusic] User connected to voice channel ${currentChannelId}, requesting music state`);
            socket.emit('music:state-request', { channelId: currentChannelId });
        }
    });
    // ─── Soundboard SFX (short effects only, NOT music streams) ───
    socket.on('voice:soundboard', (payload) => {
        if (!payload)
            return;
        // Skip if this is a music action (handled by music:play/stop/pause/resume)
        const action = payload.action;
        if (action === 'stop' || action === 'pause' || action === 'resume')
            return;
        if (payload.audioUrl || payload.url)
            return; // Music streams handled by music:play
        // Only play synthesized SFX for soundboard items
        const botUid = String(payload.userId || '370846327506079745');
        setBotSpeaking(botUid, true);
        setTimeout(() => setBotSpeaking(botUid, false), 2000);
    });
    // 12. Real-time Speaking Indicators for Bot & Participants
    socket.on('voice:speaking', ({ userId, speaking }) => {
        if (userId) {
            setBotSpeaking(userId, !!speaking);
        }
    });
    // Immediate start of Last.fm live scrobble detection
    try {
        LastFmService_1.lastFmService.pollNowPlaying();
    }
    catch { }
};
exports.initRealtimeSync = initRealtimeSync;
